import { Avatar, TableBody, TableItem, Text, HStack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { SentOrderDetailResponseNormalized } from '../../../../../store/rateManagement/selector';
import { useDeviceType } from '../../constants';

import { CardItem } from './CardItem';
import { columnWidthsDesktop, columnWidthsTablet } from './constants';

export declare namespace TableCustomerDetailItemTypes {
  export interface Props {
    item: Utils.ArrayElement<SentOrderDetailResponseNormalized['validCustomers']>;
    isLoading?: boolean;
  }
}

export const TableCustomerDetailItem: React.FC<TableCustomerDetailItemTypes.Props> = ({
  item,
  isLoading,
}) => {
  const deviceType = useDeviceType();

  const responsiveItem = useBreakpointValue({
    base: <CardItem isLoading={!!isLoading} item={item} />,
    sm: (
      <TableBody testId="table-body">
        <TableItem
          align="start"
          width={
            deviceType === 'desktop' ? columnWidthsDesktop.personal : columnWidthsTablet.personal
          }
          testId="header-clients">
          <HStack width="100%" alignItems="center" flex={1}>
            <Box alignItems="center" mr={Spacing['inset.xs']}>
              <Avatar
                testId="table-customer-suitability"
                bgColor={'linechart.principal'}
                initials={item.customerNameNormalized}
                textColor={'neutral.white'}
              />
            </Box>

            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              flex={1}
              textAlign="start">
              {item.customerNameNormalized}
            </Text>
          </HStack>
        </TableItem>

        <TableItem
          align="start"
          width={
            deviceType === 'desktop' ? columnWidthsDesktop.document : columnWidthsTablet.document
          }
          testId="header-clients">
          <VStack py={Spacing['inset.xs']} justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom={Spacing['inset.nano']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              flex={1}
              textAlign="start">
              CPF/CNPJ
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.medium"
              flex={1}
              textAlign="start">
              {item.customerCpfCnpjNormalized}
            </Text>
          </VStack>
        </TableItem>

        <TableItem
          align="start"
          width={
            deviceType === 'desktop'
              ? columnWidthsDesktop.custodyAccount
              : columnWidthsTablet.custodyAccount
          }
          testId="header-custody-account">
          <VStack width="100%" justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom="4.5px"
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              mb="2.5px"
              textAlign="start">
              Conta de custódia
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.medium"
              textAlign="start">
              {item.customerCustodyAccountNormalized}
            </Text>
          </VStack>
        </TableItem>

        <TableItem
          align="start"
          width={
            deviceType === 'desktop'
              ? columnWidthsDesktop.sinacorAccount
              : columnWidthsTablet.sinacorAccount
          }
          testId="header-sinacor-account">
          <VStack width="100%" justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom="4.5px"
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              color="neutral.medium"
              mb="2.5px"
              textAlign="start">
              Conta sinacor
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.medium"
              textAlign="start">
              {item.customerSinacorAccountNormalized}
            </Text>
          </VStack>
        </TableItem>

        <TableItem
          align="start"
          width={
            deviceType === 'desktop'
              ? columnWidthsDesktop.monthCompetence
              : columnWidthsTablet.monthCompetence
          }
          testId="header-total-sent">
          <VStack py={Spacing['inset.xs']} justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom={Spacing['inset.nano']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              flex={1}
              textAlign="start">
              Mês competência
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.medium"
              textAlign="start">
              {item.managementFeeMonthNormalized}
            </Text>
          </VStack>
        </TableItem>

        <TableItem
          align="start"
          width={
            deviceType === 'desktop'
              ? columnWidthsDesktop.balanceAvailable
              : columnWidthsTablet.balanceAvailable
          }
          testId="header-total-sent">
          <VStack py={Spacing['inset.xs']} justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom={Spacing['inset.nano']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              flex={1}
              textAlign="start">
              Saldo disponível
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              flex={1}
              color="neutral.medium"
              textAlign="start">
              {item.customerAvailableBalanceNormalized}
            </Text>
          </VStack>
        </TableItem>

        <TableItem
          align="start"
          width={deviceType === 'desktop' ? columnWidthsDesktop.rate : columnWidthsTablet.rate}
          testId="header-total-sent">
          <VStack py={Spacing['inset.xs']} justifyContent="space-between" flex={1}>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              paddingBottom={Spacing['inset.nano']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              fontWeight={Typography['fontweight.semibold']}
              flex={1}
              textAlign="start">
              Taxa (R$)
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              fontWeight={Typography['fontweight.regular']}
              fontSize={Utils.Responsive.All({
                phone: Typography['fontsize.xs'],
                tablet: Typography['fontsize.xs'],
                desktop: Typography['fontsize.md'],
              })}
              color="neutral.medium"
              flex={1}
              textAlign="start">
              {item.managementFeeRateNormalized}
            </Text>
          </VStack>
        </TableItem>
      </TableBody>
    ),
  });

  return <Box flex={1}>{responsiveItem}</Box>;
};
